import React, { useState, useEffect, useRef } from 'react';
import { Calendar, Search, Plus, X } from 'lucide-react';
import type { Event } from '../types';

interface Props {
  events: Event[];
  selectedEvents: string[];
  onEventSelect: (eventId: string) => void;
  onEventRemove?: (eventId: string) => void;
  placeholder?: string;
}

export default function EventSearch({ events, selectedEvents = [], onEventSelect, onEventRemove, placeholder = 'Search events...' }: Props) {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [filteredEvents, setFilteredEvents] = useState<Event[]>([]);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // Handle clicks outside of the component
    function handleClickOutside(event: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setIsOpen(true);
    setHighlightedIndex(-1);
    setSelectedEvent(null);

    if (value.length >= 2) {
      const filtered = events
        .filter(event => 
          !selectedEvents.includes(event.id) &&
          (event.name.toLowerCase().includes(value.toLowerCase()) ||
           formatDate(event.date).includes(value))
        )
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
      setFilteredEvents(filtered);
    } else {
      setFilteredEvents([]);
    }
  };

  const handleSelect = (event: Event) => {
    setSearchTerm(event.name);
    setSelectedEvent(event);
    setIsOpen(false);
    setHighlightedIndex(-1);
  };

  const handleAddEvent = () => {
    if (selectedEvent) {
      onEventSelect(selectedEvent.id);
      setSearchTerm('');
      setSelectedEvent(null);
      inputRef.current?.focus();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen || filteredEvents.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < filteredEvents.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : 0);
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0) {
          handleSelect(filteredEvents[highlightedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        inputRef.current?.blur();
        break;
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const selectedEventsList = events.filter(event => selectedEvents.includes(event.id));

  return (
    <div ref={wrapperRef} className="space-y-3">
      {/* Selected Events */}
      {selectedEventsList.length > 0 && (
        <div className="space-y-2">
          {selectedEventsList.map(event => (
            <div
              key={event.id}
              className="flex items-center justify-between text-sm bg-gray-50 p-2 rounded"
            >
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-gray-500" />
                <span className="font-medium">{event.name}</span>
                <span className="text-gray-500">·</span>
                <span className="text-gray-500">{formatDate(event.date)}</span>
              </div>
              {onEventRemove && (
                <button
                  onClick={() => onEventRemove(event.id)}
                  className="text-gray-400 hover:text-red-600"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Search Input */}
      <div className="flex gap-2">
        <div className="relative flex-1">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
          <input
            ref={inputRef}
            type="text"
            value={searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-gray-500 focus:border-gray-500 sm:text-sm"
            role="combobox"
            aria-expanded={isOpen}
            aria-controls="event-search-listbox"
            aria-activedescendant={highlightedIndex >= 0 ? `event-${filteredEvents[highlightedIndex].id}` : undefined}
          />

          {/* Dropdown */}
          {isOpen && searchTerm.length >= 2 && filteredEvents.length > 0 && (
            <div 
              id="event-search-listbox"
              role="listbox"
              className="absolute left-0 right-0 z-10 mt-1 bg-white rounded-md shadow-lg border border-gray-200 max-h-60 overflow-y-auto"
              style={{ maxWidth: '400px' }}
            >
              {filteredEvents.map((event, index) => (
                <button
                  key={event.id}
                  id={`event-${event.id}`}
                  role="option"
                  aria-selected={index === highlightedIndex}
                  onClick={() => handleSelect(event)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                  className={`w-full px-4 py-2 text-left flex items-center gap-3 hover:bg-gray-50 ${
                    index === highlightedIndex ? 'bg-gray-50' : ''
                  }`}
                >
                  <Calendar className="w-5 h-5 text-gray-400" />
                  <div>
                    <div className="font-medium text-gray-900">{event.name}</div>
                    <div className="text-sm text-gray-500">{formatDate(event.date)}</div>
                  </div>
                </button>
              ))}
            </div>
          )}

          {isOpen && searchTerm.length >= 2 && filteredEvents.length === 0 && (
            <div className="absolute left-0 right-0 z-10 mt-1 bg-white rounded-md shadow-lg border border-gray-200 p-4 text-center text-gray-500" style={{ maxWidth: '400px' }}>
              No events found
            </div>
          )}
        </div>
        <button
          onClick={handleAddEvent}
          disabled={!selectedEvent}
          className="flex items-center justify-center px-3 py-2 bg-gray-900 text-white rounded-md hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Plus className="w-4 h-4" />
        </button>
      </div>

      {searchTerm.length > 0 && searchTerm.length < 2 && (
        <p className="mt-1 text-sm text-gray-500">
          Type at least 2 characters to search
        </p>
      )}
    </div>
  );
}